import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled from 'styled-components';
import { FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';
// Alert Component
export const Alert = styled.div `
    padding: 1rem 1.5rem;
    border-radius: 8px;
    background: ${props => props.type === 'error' ? '#fee' : '#efe'};
    color: ${props => props.type === 'error' ? '#c00' : '#060'};
    border: 1px solid ${props => props.type === 'error' ? '#fcc' : '#cfc'};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
`;
export const CloseButton = styled.button `
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: inherit;
    padding: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.6;

    &:hover {
        opacity: 1;
    }
`;
// Button Component (uses $primary transient prop to avoid DOM forwarding)
export const Button = styled.button `
    padding: 0.75rem 1.5rem;
    border-radius: 6px;
    border: 1px solid ${props => props.$primary ? 'var(--accent-gold)' : 'var(--border-color)'};
    background: ${props => props.$primary ? 'var(--accent-gold)' : 'var(--bg-secondary)'};
    color: ${props => props.$primary ? 'white' : 'var(--text-primary)'};
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    font-size: 0.9rem;
    white-space: nowrap;

    &:hover:not(:disabled) {
        background: ${props => props.$primary ? 'var(--accent-gold-dark)' : 'var(--bg-tertiary)'};
        transform: translateY(-1px);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    @media (max-width: 768px) {
        padding: 0.6rem 1rem;
        font-size: 0.85rem;
    }

    @media (max-width: 375px) {
        padding: 0.5rem 0.75rem;
        font-size: 0.8rem;
    }
`;
// Stats Grid
export const StatsGrid = styled.div `
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 1rem;
    }

    @media (max-width: 375px) {
        grid-template-columns: 1fr;
        gap: 0.75rem;
    }
`;
export const StatCard = styled.div `
    background: linear-gradient(135deg,
        ${props => props.color || '#6b7280'}15 0%,
        ${props => props.color || '#6b7280'}05 100%
    );
    border: 1px solid ${props => props.color || '#6b7280'}30;
    border-left: 4px solid ${props => props.color || '#6b7280'};
    border-radius: 12px;
    padding: 1.5rem;
    position: relative;
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 24px ${props => props.color || '#6b7280'}20;
        border-color: ${props => props.color || '#6b7280'};
    }

    @media (max-width: 768px) {
        padding: 1.25rem;
        border-radius: 10px;
    }

    @media (max-width: 375px) {
        padding: 1rem;
        border-radius: 8px;
    }
`;
export const StatIcon = styled.div `
    position: absolute;
    top: 1rem;
    right: 1rem;
    font-size: 2rem;
    color: ${props => props.color || '#6b7280'};
    opacity: 0.25;

    @media (max-width: 768px) {
        font-size: 1.75rem;
        top: 0.75rem;
        right: 0.75rem;
    }
`;
export const StatLabel = styled.div `
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--text-tertiary);
    font-weight: 600;
    margin-bottom: 0.5rem;

    @media (max-width: 768px) {
        font-size: 0.7rem;
    }

    @media (max-width: 375px) {
        font-size: 0.65rem;
    }
`;
export const StatValue = styled.div `
    font-size: 2rem;
    font-weight: 700;
    color: var(--text-primary);
    font-family: 'JetBrains Mono', monospace;
    line-height: 1;

    @media (max-width: 768px) {
        font-size: 1.75rem;
    }

    @media (max-width: 375px) {
        font-size: 1.5rem;
    }
`;
// Table Components
export const TableContainer = styled.div `
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    overflow-x: auto;
    max-width: 100%;
    -webkit-overflow-scrolling: touch;

    @media (max-width: 768px) {
        border-radius: 6px;
    }
`;
export const Table = styled.table `
    width: 100%;
    border-collapse: collapse;
    min-width: 600px;

    @media (max-width: 375px) {
        min-width: 500px;
    }
`;
export const Th = styled.th `
    text-align: left;
    padding: 1rem;
    background: var(--bg-tertiary);
    border-bottom: 1px solid var(--border-color);
    font-weight: 600;
    font-size: 0.875rem;
    color: var(--text-primary);
    white-space: nowrap;

    @media (max-width: 768px) {
        padding: 0.75rem;
        font-size: 0.8rem;
    }

    @media (max-width: 375px) {
        padding: 0.5rem;
        font-size: 0.75rem;
    }
`;
// Sortable Table Header
const SortableThBase = styled.th `
    text-align: left;
    padding: 1rem;
    background: var(--bg-tertiary);
    border-bottom: 1px solid var(--border-color);
    font-weight: 600;
    font-size: 0.875rem;
    color: var(--text-primary);
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
    transition: all 0.2s;

    &:hover {
        background: var(--bg-secondary);
        color: var(--accent-gold);
    }

    ${props => props.$active && `
        color: var(--accent-gold);
    `}

    @media (max-width: 768px) {
        padding: 0.75rem;
        font-size: 0.8rem;
    }

    @media (max-width: 375px) {
        padding: 0.5rem;
        font-size: 0.75rem;
    }
`;
const SortableThContent = styled.div `
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;
const SortIcon = styled.span `
    display: inline-flex;
    align-items: center;
    font-size: 0.75rem;
    opacity: ${props => props.$active ? 1 : 0.3};
    color: ${props => props.$active ? 'var(--accent-gold)' : 'var(--text-tertiary)'};
    transition: opacity 0.2s, color 0.2s;

    ${SortableThBase}:hover & {
        opacity: 0.7;
    }
`;
export const SortableTh = ({ children, sortKey, currentSortKey, sortDirection, onSort }) => {
    const isActive = currentSortKey === sortKey;
    const handleClick = () => {
        onSort(sortKey);
    };
    const renderIcon = () => {
        if (!isActive || !sortDirection) {
            return _jsx(FaSort, {});
        }
        return sortDirection === 'asc' ? _jsx(FaSortUp, {}) : _jsx(FaSortDown, {});
    };
    return (_jsx(SortableThBase, { "$active": isActive, onClick: handleClick, children: _jsxs(SortableThContent, { children: [children, _jsx(SortIcon, { "$active": isActive, children: renderIcon() })] }) }));
};
export const Td = styled.td `
    padding: 1rem;
    border-bottom: 1px solid var(--border-color);
    font-size: 0.875rem;
    color: var(--text-primary);

    &:last-child {
        text-align: right;
    }

    @media (max-width: 768px) {
        padding: 0.75rem;
        font-size: 0.8rem;
    }

    @media (max-width: 375px) {
        padding: 0.5rem;
        font-size: 0.75rem;
    }
`;
export const Tr = styled.tr `
    &:hover {
        background: var(--bg-tertiary);
    }

    &:last-child td {
        border-bottom: none;
    }
`;
// Filter Components
export const FiltersContainer = styled.div `
    display: grid;
    grid-template-columns: 2fr 1fr 1fr auto;
    gap: 1rem;
    padding: 1rem;
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    box-sizing: border-box;

    @media (max-width: 1024px) {
        grid-template-columns: 1fr 1fr;
    }

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
        padding: 0.75rem;
    }

    @media (max-width: 375px) {
        padding: 0.5rem;
        gap: 0.75rem;
        border-radius: 6px;
    }
`;
export const FilterGroup = styled.div `
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;
export const Label = styled.label `
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-primary);
`;
export const Input = styled.input `
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    font-size: 0.875rem;
    background: var(--bg-primary);
    color: var(--text-primary);
    width: 100%;
    box-sizing: border-box;

    &::placeholder {
        color: var(--text-tertiary);
    }

    &:focus {
        outline: none;
        border-color: var(--accent-gold);
        box-shadow: 0 0 0 3px var(--accent-gold-bg);
    }

    @media (max-width: 375px) {
        font-size: 0.8rem;
        padding: 0.5rem;
    }
`;
export const Select = styled.select `
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    font-size: 0.875rem;
    background: var(--bg-primary);
    color: var(--text-primary);
    cursor: pointer;
    width: 100%;
    box-sizing: border-box;

    option {
        background: var(--bg-secondary);
        color: var(--text-primary);
    }

    &:focus {
        outline: none;
        border-color: var(--accent-gold);
        box-shadow: 0 0 0 3px var(--accent-gold-bg);
    }

    @media (max-width: 375px) {
        font-size: 0.8rem;
        padding: 0.5rem;
    }
`;
// Status Badge
export const StatusBadge = styled.span `
    padding: 0.25rem 0.75rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;
    background: ${props => {
    switch (props.status.toLowerCase()) {
        case 'active': return '#d1fae5';
        case 'paid': return '#d1fae5';
        case 'verified': return '#d1fae5';
        case 'inactive': return '#fee2e2';
        case 'unpaid': return '#fef3c7';
        case 'partial': return '#fef3c7';
        case 'pending': return '#fef3c7';
        case 'suspended': return '#fee2e2';
        default: return '#f3f4f6';
    }
}};
    color: ${props => {
    switch (props.status.toLowerCase()) {
        case 'active': return '#065f46';
        case 'paid': return '#065f46';
        case 'verified': return '#065f46';
        case 'inactive': return '#991b1b';
        case 'unpaid': return '#92400e';
        case 'partial': return '#92400e';
        case 'pending': return '#92400e';
        case 'suspended': return '#991b1b';
        default: return '#374151';
    }
}};
`;
// Empty State
export const EmptyState = styled.div `
    text-align: center;
    padding: 4rem 2rem;
    color: var(--text-secondary);
`;
export const EmptyStateIcon = styled.div `
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
`;
export const EmptyStateText = styled.p `
    font-size: 1rem;
    margin: 0;
`;
